import './styles.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Snackbar,
  Alert,
  Stack,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';

import { getUserDataFromToken } from '../../../Utils/urlAuthHandler';
import { initializeWorkingDaysConfig } from '../../../Utils/workingDaysConfig';
import { getHolidayDetails } from '../../../Utils/holidays';
import { getPlannerMonth, savePlannerMonth } from '../../../Action/api';

import WeekNavigator from './components/WeekNavigator';
import SummaryCards from './components/SummaryCards';
import ValidationWarnings from './components/ValidationWarnings';
import WeeklyGrid from './components/WeeklyGrid';
import ActivityFormDialog from './components/ActivityFormDialog';
import BulkDayDialog from './components/BulkDayDialog';
import WeekPlannerDialog from './components/WeekPlannerDialog';
import {
  getWeekStart,
  getWeekKey,
  getWeekDates,
  getDayKey,
  getMonthKey,
  goToNextWeek,
  goToPreviousWeek,
  shiftDayKey,
  isDateToday,
  calculateSummary,
  getValidationWarnings,
} from './plannerUtils';

const EMPTY_WEEK_META = { status: 'Draft', approvedBy: null };
const EMPTY_MONTH_DOC = { activities: [], weeks: {} };

const newId = () => `act-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const Planner = () => {
  // The planner API keys data by the token's `uId` claim (e.g. "UID07") — `userId` on the token
  // is the login email, not the employee id the backend expects here.
  const { userName, uId: plannerUserId, depName, roleName } = getUserDataFromToken();

  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));
  const [intervalMinutes, setIntervalMinutes] = useState(30);

  // Raw, backend-shaped cache: one entry per fetched calendar month ("YYYY-MM" -> { activities, weeks }).
  const [monthDocs, setMonthDocs] = useState({});
  // Months we've already tried and failed to load — excluded from auto-retry so a persistent
  // server error can't spin the fetch effect forever; cleared only by the Retry button.
  const [failedMonths, setFailedMonths] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const fetchingRef = useRef(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitialValues, setDialogInitialValues] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDialogDay, setBulkDialogDay] = useState(null);
  const [weekDialogOpen, setWeekDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    initializeWorkingDaysConfig('IN');
  }, []);

  const realCurrentWeekKey = useMemo(() => getWeekKey(getWeekStart(new Date())), []);
  const weekKey = getWeekKey(currentWeekStart);
  const weekDates = useMemo(() => getWeekDates(currentWeekStart), [currentWeekStart]);
  // A displayed (Mon-Sat) week can straddle two calendar months — the backend partitions by month,
  // so a week's activities may need to be fetched from and split across up to two month documents.
  const touchedMonths = useMemo(
    () => Array.from(new Set(weekDates.map((d) => getMonthKey(d)))),
    [weekDates]
  );
  // The week's Draft/Submitted/Approved/Rejected status lives on whichever month contains Monday,
  // so it has exactly one home even when the week's days span two months.
  const owningMonth = getMonthKey(currentWeekStart);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Fetch any months this week touches that aren't cached yet. Months that already failed are
  // excluded here (via `failedMonths`) so a persistent server error can't loop this forever —
  // only the Retry button clears that and lets a month be attempted again.
  useEffect(() => {
    const missing = touchedMonths.filter(
      (m) => monthDocs[m] === undefined && !failedMonths.has(m) && !fetchingRef.current.has(m)
    );
    if (missing.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    missing.forEach((m) => fetchingRef.current.add(m));

    Promise.all(
      missing.map(async (m) => {
        try {
          const doc = await getPlannerMonth(plannerUserId, m);
          return { month: m, doc: doc || { activities: [], weeks: {} }, ok: true };
        } catch (err) {
          console.error(`Error loading planner month ${m}:`, err);
          return { month: m, ok: false };
        } finally {
          fetchingRef.current.delete(m);
        }
      })
    ).then((results) => {
      const succeeded = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);

      // Only touch state when there's actually something to change — an update that always
      // produces a "new but equal" object would re-trigger this effect on every failure forever.
      if (succeeded.length > 0) {
        setMonthDocs((prev) => {
          const next = { ...prev };
          succeeded.forEach((r) => {
            next[r.month] = r.doc;
          });
          return next;
        });
      }
      if (failed.length > 0) {
        setFailedMonths((prev) => new Set([...prev, ...failed.map((r) => r.month)]));
        setLoadError(`Couldn't load planner data for ${failed.map((r) => r.month).join(', ')}.`);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchedMonths, plannerUserId, monthDocs, failedMonths]);

  const retryLoad = () => {
    setLoadError(null);
    setFailedMonths((prev) => {
      const next = new Set(prev);
      touchedMonths.forEach((m) => next.delete(m));
      return next;
    });
  };

  // Merge the touched month(s) down into the shape every existing component expects.
  const weekActivities = useMemo(() => {
    const acts = [];
    touchedMonths.forEach((m) => {
      const doc = monthDocs[m];
      if (!doc) return;
      doc.activities.forEach((a) => {
        if (weekDates.some((d) => getDayKey(d) === a.day)) acts.push(a);
      });
    });
    return acts;
  }, [monthDocs, touchedMonths, weekDates]);

  const weekMeta = monthDocs[owningMonth]?.weeks?.[weekKey] || EMPTY_WEEK_META;
  const weekData = { ...weekMeta, activities: weekActivities };
  // Only a manager-approved week is locked — a Submitted week can still be edited (nothing's
  // final until it's actually Approved), and Rejected/Draft are always editable.
  const readOnly = weekData.status === 'Approved';

  const summary = useMemo(() => calculateSummary(weekData.activities), [weekData.activities]);
  const warnings = useMemo(
    () => getValidationWarnings(weekData.activities, weekDates),
    [weekData.activities, weekDates]
  );

  const holidaysThisWeek = useMemo(
    () => weekDates.map((d) => getHolidayDetails(d)).filter(Boolean),
    [weekDates]
  );

  const persistMonth = async (monthKey, doc) => {
    try {
      await savePlannerMonth(plannerUserId, monthKey, doc);
    } catch (err) {
      console.error(`Error saving planner month ${monthKey}:`, err);
      showSnackbar('Failed to save your changes to the server. Please try again.', 'error');
    }
  };

  /**
   * The single funnel every mutation goes through. Takes the merged week view, applies `updater`,
   * then splits the result back across whichever month document(s) this week's days live in —
   * always re-sending each touched month's *whole* document so other days/weeks in that month
   * aren't lost.
   */
  const updateWeek = (updater) => {
    const newWeekData = updater(weekData);
    const newActivitiesByMonth = {};
    newWeekData.activities.forEach((a) => {
      const m = getMonthKey(a.day);
      (newActivitiesByMonth[m] = newActivitiesByMonth[m] || []).push(a);
    });

    const nextMonthDocs = { ...monthDocs };
    touchedMonths.forEach((m) => {
      const existingDoc = monthDocs[m] || EMPTY_MONTH_DOC;
      const keptActivities = existingDoc.activities.filter(
        (a) => !(weekDates.some((d) => getDayKey(d) === a.day) && getMonthKey(a.day) === m)
      );
      const updatedDoc = {
        ...existingDoc,
        activities: [...keptActivities, ...(newActivitiesByMonth[m] || [])],
        weeks:
          m === owningMonth
            ? { ...existingDoc.weeks, [weekKey]: { status: newWeekData.status, approvedBy: newWeekData.approvedBy } }
            : existingDoc.weeks,
      };
      nextMonthDocs[m] = updatedDoc;
    });

    setMonthDocs(nextMonthDocs);
    touchedMonths.forEach((m) => persistMonth(m, nextMonthDocs[m]));
  };

  const handleOpenAddDialog = (day, startMinutes) => {
    if (readOnly) return;
    setDialogInitialValues({ day, startMinutes, endMinutes: Math.min(startMinutes + 60, 24 * 60) });
    setDialogOpen(true);
  };

  const handleAddButtonClick = () => {
    if (readOnly) return;
    setDialogInitialValues(null);
    setDialogOpen(true);
  };

  const handleEditActivity = (activity) => {
    if (readOnly) return;
    setDialogInitialValues(activity);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogInitialValues(null);
  };

  const handleOpenBulkDialog = () => {
    if (readOnly) return;
    const defaultDay = weekDates.find((d) => isDateToday(d)) || weekDates[0];
    setBulkDialogDay(getDayKey(defaultDay));
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => setBulkDialogOpen(false);

  const handleSaveDay = (dayKey, dayActivities) => {
    const withIds = dayActivities.map((a) => ({ ...a, id: a.id || newId() }));
    updateWeek((week) => ({
      ...week,
      activities: [...week.activities.filter((a) => a.day !== dayKey), ...withIds],
    }));
    showSnackbar(`Saved ${dayActivities.length} ${dayActivities.length === 1 ? 'activity' : 'activities'} for the day.`);
    setBulkDialogOpen(false);
  };

  const handleSaveActivity = (data) => {
    updateWeek((week) => {
      const activities = data.id
        ? week.activities.map((a) => (a.id === data.id ? { ...a, ...data } : a))
        : [...week.activities, { ...data, id: newId() }];
      return { ...week, activities };
    });
    showSnackbar(data.id ? 'Activity updated.' : 'Activity added.');
    handleCloseDialog();
  };

  const handleDeleteActivity = (id) => {
    updateWeek((week) => ({ ...week, activities: week.activities.filter((a) => a.id !== id) }));
    showSnackbar('Activity deleted.', 'info');
    handleCloseDialog();
  };

  const handleActivityMove = (id, changes) => {
    updateWeek((week) => ({
      ...week,
      activities: week.activities.map((a) => (a.id === id ? { ...a, ...changes } : a)),
    }));
  };

  // Duplicates an activity right after itself on the same day, so cloning + a quick drag/edit
  // is faster than filling the Add Activity form out from scratch every time.
  const handleCloneActivity = (activity) => {
    if (readOnly) return;
    const duration = activity.endMinutes - activity.startMinutes;
    const newStart = Math.min(activity.endMinutes, 24 * 60 - duration);
    const clone = { ...activity, id: newId(), startMinutes: newStart, endMinutes: newStart + duration };
    updateWeek((week) => ({ ...week, activities: [...week.activities, clone] }));
    showSnackbar('Activity cloned — drag it to adjust the time.');
  };

  // Drag one activity onto another (same day) to swap their times — "move 9:00 to 10:00 and
  // 10:00 to 9:00" in one drag, without retyping either one.
  const handleSwapActivityTimes = (idA, idB) => {
    if (readOnly || idA === idB) return;
    updateWeek((week) => {
      const a = week.activities.find((x) => x.id === idA);
      const b = week.activities.find((x) => x.id === idB);
      if (!a || !b) return week;
      return {
        ...week,
        activities: week.activities.map((act) => {
          if (act.id === idA) return { ...act, startMinutes: b.startMinutes, endMinutes: b.endMinutes };
          if (act.id === idB) return { ...act, startMinutes: a.startMinutes, endMinutes: a.endMinutes };
          return act;
        }),
      };
    });
    showSnackbar('Swapped activity times.');
  };

  // Drag an activity onto a different day column — keeps its time, just moves the day.
  const handleMoveActivityToDay = (id, newDay) => {
    if (readOnly) return;
    updateWeek((week) => ({
      ...week,
      activities: week.activities.map((a) => (a.id === id ? { ...a, day: newDay } : a)),
    }));
    showSnackbar('Activity moved.');
  };

  const handleCopyPreviousWeek = async () => {
    if (readOnly) return;
    const previousWeekStart = goToPreviousWeek(currentWeekStart);
    const previousWeekDates = getWeekDates(previousWeekStart);
    const previousMonths = Array.from(new Set(previousWeekDates.map((d) => getMonthKey(d))));

    let docsForPrevious = monthDocs;
    const missing = previousMonths.filter((m) => docsForPrevious[m] === undefined);
    if (missing.length > 0) {
      const fetched = await Promise.all(
        missing.map(async (m) => [m, (await getPlannerMonth(plannerUserId, m).catch(() => null)) || EMPTY_MONTH_DOC])
      );
      docsForPrevious = { ...monthDocs };
      fetched.forEach(([m, doc]) => {
        docsForPrevious[m] = doc;
      });
      setMonthDocs(docsForPrevious);
    }

    const previousActivities = [];
    previousMonths.forEach((m) => {
      const doc = docsForPrevious[m];
      if (!doc) return;
      doc.activities.forEach((a) => {
        if (previousWeekDates.some((d) => getDayKey(d) === a.day)) previousActivities.push(a);
      });
    });

    if (previousActivities.length === 0) {
      showSnackbar('No activities found in the previous week to copy.', 'warning');
      return;
    }
    const copied = previousActivities.map((a) => ({
      ...a,
      id: newId(),
      day: shiftDayKey(a.day, 7),
    }));
    updateWeek((week) => ({ ...week, activities: copied }));
    showSnackbar('Previous week copied into this draft. Review and submit when ready.', 'info');
  };

  const hasOverlapWarning = warnings.some((w) => w.severity === 'error');

  const handleSubmitWeek = () => {
    if (weekData.activities.length === 0) {
      showSnackbar('Add at least one activity before submitting.', 'warning');
      return;
    }
    if (hasOverlapWarning) {
      showSnackbar('Fix the overlapping activities before submitting this week.', 'error');
      return;
    }
    updateWeek((week) => ({ ...week, status: 'Submitted' }));
    showSnackbar('Weekly plan submitted for manager approval.');
  };

  return (
    <div className="planner-page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom className="page-title" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Planner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Plan your working week — projects, tasks, meetings and breaks.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip icon={<BadgeIcon />} label={userName || 'Employee'} size="small" />
          {depName && <Chip icon={<CalendarMonthIcon />} label={depName} size="small" variant="outlined" />}
          {roleName && <Chip label={roleName} size="small" variant="outlined" />}
        </Stack>
      </Box>

      <WeekNavigator
        weekStart={currentWeekStart}
        isCurrentWeek={weekKey === realCurrentWeekKey}
        onPrevious={() => setCurrentWeekStart((d) => goToPreviousWeek(d))}
        onNext={() => setCurrentWeekStart((d) => goToNextWeek(d))}
        onToday={() => setCurrentWeekStart(getWeekStart(new Date()))}
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: '10px' }}
          action={
            <Button color="inherit" size="small" onClick={retryLoad}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading your week…
          </Typography>
        </Box>
      ) : (
        <>
          <SummaryCards
            plannedWorkMinutes={summary.plannedWorkMinutes}
            completedWorkMinutes={summary.completedWorkMinutes}
            remainingWorkMinutes={summary.remainingWorkMinutes}
            plannedBreakMinutes={summary.plannedBreakMinutes}
            status={weekData.status}
          />

          {weekData.status === 'Submitted' && (
            <Alert severity="info" variant="filled" sx={{ mt: 1, mb: 2, borderRadius: '10px' }}>
              This week's plan has been submitted and is awaiting manager approval.
            </Alert>
          )}
          {weekData.status === 'Approved' && (
            <Alert severity="success" variant="filled" sx={{ mt: 1, mb: 2, borderRadius: '10px' }}>
              This week's plan was approved{weekData.approvedBy ? ` by ${weekData.approvedBy}` : ''}.
            </Alert>
          )}
          {weekData.status === 'Rejected' && (
            <Alert severity="error" variant="filled" sx={{ mt: 1, mb: 2, borderRadius: '10px' }}>
              This week's plan was rejected. Make the required changes and resubmit.
            </Alert>
          )}
          {holidaysThisWeek.length > 0 && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: '10px' }}>
              Public holiday this week: {holidaysThisWeek.map((h) => h.name).join(', ')}.
            </Alert>
          )}

          <ValidationWarnings warnings={warnings} />

          <Paper elevation={1} className="planner-toolbar">
            <Typography variant="subtitle1">Weekly Planner</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyPreviousWeek}
                disabled={readOnly}
              >
                Copy Previous Week
              </Button>
              <Button variant="outlined" startIcon={<ViewDayIcon />} onClick={handleOpenBulkDialog} disabled={readOnly}>
                Plan Full Day
              </Button>
              <Button variant="outlined" startIcon={<ViewWeekIcon />} onClick={() => setWeekDialogOpen(true)}>
                Plan Full Week
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddButtonClick} disabled={readOnly}>
                Add Activity
              </Button>
            </Stack>
          </Paper>

          <Paper elevation={1} sx={{ p: 2, borderRadius: '10px' }}>
            <WeeklyGrid
              weekDates={weekDates}
              activities={weekData.activities}
              intervalMinutes={intervalMinutes}
              onIntervalChange={setIntervalMinutes}
              readOnly={readOnly}
              onActivityMove={handleActivityMove}
              onSlotClick={handleOpenAddDialog}
              onActivityClick={handleEditActivity}
              onActivityClone={handleCloneActivity}
            />
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 3, mb: 4 }}>
            {!readOnly && hasOverlapWarning && (
              <Typography variant="body2" color="error">
                Resolve the overlapping activities above before submitting.
              </Typography>
            )}
            <Tooltip title={!readOnly && hasOverlapWarning ? 'Fix the overlapping activities highlighted above first.' : ''}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<SendIcon />}
                  onClick={handleSubmitWeek}
                  disabled={readOnly || hasOverlapWarning}
                >
                  Submit Weekly Plan
                </Button>
              </span>
            </Tooltip>
          </Box>
        </>
      )}

      <ActivityFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveActivity}
        onDelete={handleDeleteActivity}
        onDuplicate={(activity) => {
          handleCloneActivity(activity);
          handleCloseDialog();
        }}
        initialValues={dialogInitialValues}
        weekDates={weekDates}
      />

      <BulkDayDialog
        open={bulkDialogOpen}
        onClose={handleCloseBulkDialog}
        weekDates={weekDates}
        initialDay={bulkDialogDay}
        getDayActivities={(dayKey) => weekData.activities.filter((a) => a.day === dayKey)}
        onSaveDay={handleSaveDay}
      />

      <WeekPlannerDialog
        open={weekDialogOpen}
        onClose={() => setWeekDialogOpen(false)}
        weekStart={currentWeekStart}
        weekDates={weekDates}
        activities={weekData.activities}
        readOnly={readOnly}
        onAddActivity={handleOpenAddDialog}
        onEditActivity={handleEditActivity}
        onDeleteActivity={handleDeleteActivity}
        onCloneActivity={handleCloneActivity}
        onSwapActivities={handleSwapActivityTimes}
        onMoveActivityToDay={handleMoveActivityToDay}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Planner;
